import React, { useEffect, useMemo, useState } from "react";
import {
  getAmlPendingReview,
  approveAmlDocument,
  rejectAmlDocument
} from "../api/client.js";

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

export default function CrmAmlReviewPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notesById, setNotesById] = useState({});
  const [rejectById, setRejectById] = useState({});
  const [actionState, setActionState] = useState({});

  const load = () => {
    setLoading(true);
    setError("");
    getAmlPendingReview()
      .then((response) => {
        const data = response?.data ?? response?.items ?? response ?? [];
        setItems(Array.isArray(data) ? data : []);
      })
      .catch((err) => setError(err.message || "Unable to load AML queue"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleApprove = async (id) => {
    setActionState((prev) => ({ ...prev, [id]: "approving" }));
    try {
      await approveAmlDocument(id, { notes: notesById[id] || "" });
      load();
    } catch (err) {
      setError(err.message || "Approval failed");
    } finally {
      setActionState((prev) => ({ ...prev, [id]: null }));
    }
  };

  const handleReject = async (id) => {
    const reason = rejectById[id]?.reason;
    const rejectionCode = rejectById[id]?.code;
    if (!reason || !rejectionCode) {
      setError("Provide a rejection code and reason before rejecting.");
      return;
    }
    setActionState((prev) => ({ ...prev, [id]: "rejecting" }));
    try {
      await rejectAmlDocument(id, {
        reason,
        rejectionCode,
        notifySeller: true
      });
      load();
    } catch (err) {
      setError(err.message || "Rejection failed");
    } finally {
      setActionState((prev) => ({ ...prev, [id]: null }));
    }
  };

  return (
    <div className="space-y-6">
      <header className="rounded-3xl border border-white/10 bg-white/5 p-6">
        <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Compliance queue</p>
        <h1 className="text-3xl font-semibold text-white">AML Review</h1>
        <p className="text-sm text-white/70">
          Review documents awaiting approval. Approve to progress onboarding or reject with a reason.
        </p>
      </header>

      {error && (
        <div className="rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6 text-sm text-white/60">
          Loading AML queue…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6 text-sm text-white/60">
          No documents pending review.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((item) => (
            <div key={item.id} className="rounded-3xl border border-white/10 bg-white/5 p-6 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Document</p>
                  <h2 className="text-lg font-semibold text-white">{item.documentId}</h2>
                  <p className="text-xs text-white/60">Onboarding: {item.onboardingId || "—"}</p>
                </div>
                <div className="text-right text-xs text-white/60">
                  <p>Status: {item.amlStatus}</p>
                  <p>Score: {item.overallScore ?? 0}</p>
                  <p>Submitted: {formatDate(item.createdAt)}</p>
                </div>
              </div>

              {item.issues?.length > 0 && (
                <div className="rounded-2xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-xs text-amber-200">
                  <p className="font-semibold mb-2">Flagged issues</p>
                  <ul className="list-disc ml-4 space-y-1">
                    {item.issues.map((issue, idx) => (
                      <li key={idx}>{issue.message || issue.code || "Issue flagged"}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-2">
                <label className="text-xs text-white/70">
                  Approval notes
                  <textarea
                    className="mt-2 w-full rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-xs text-white"
                    rows={3}
                    value={notesById[item.id] || ""}
                    onChange={(event) =>
                      setNotesById((prev) => ({ ...prev, [item.id]: event.target.value }))
                    }
                    placeholder="Optional notes for approval"
                  />
                </label>

                <div className="space-y-2">
                  <label className="text-xs text-white/70">
                    Rejection code
                    <input
                      className="mt-2 w-full rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-xs text-white"
                      value={rejectById[item.id]?.code || ""}
                      onChange={(event) =>
                        setRejectById((prev) => ({
                          ...prev,
                          [item.id]: { ...prev[item.id], code: event.target.value }
                        }))
                      }
                      placeholder="DOC_BLUR, EXPIRED_ID, MISSING_PAGE..."
                    />
                  </label>
                  <label className="text-xs text-white/70">
                    Rejection reason
                    <textarea
                      className="mt-2 w-full rounded-xl border border-white/15 bg-black/40 px-3 py-2 text-xs text-white"
                      rows={3}
                      value={rejectById[item.id]?.reason || ""}
                      onChange={(event) =>
                        setRejectById((prev) => ({
                          ...prev,
                          [item.id]: { ...prev[item.id], reason: event.target.value }
                        }))
                      }
                      placeholder="Provide a clear reason for rejection"
                    />
                  </label>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 justify-end">
                <button
                  onClick={() => handleReject(item.id)}
                  disabled={actionState[item.id] === "rejecting"}
                  className="rounded-xl border border-rose-400/50 bg-rose-500/10 px-4 py-2 text-xs text-rose-200 hover:bg-rose-500/20 disabled:opacity-50"
                >
                  {actionState[item.id] === "rejecting" ? "Rejecting…" : "Reject"}
                </button>
                <button
                  onClick={() => handleApprove(item.id)}
                  disabled={actionState[item.id] === "approving"}
                  className="rounded-xl border border-emerald-400/50 bg-emerald-500/10 px-4 py-2 text-xs text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50"
                >
                  {actionState[item.id] === "approving" ? "Approving…" : "Approve"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
